import * as Dialog from '@radix-ui/react-dialog'
import { type ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { InhertaIcon, InhertaWordmark } from '../brand/InhertaMark'
import { cn } from '../cn'
import { useIsBelowLg } from '../useMediaQuery'
import { SidebarNav, type SidebarNavItem } from './SidebarNav'
import { TopBar, type TopBarBreadcrumbItem } from './TopBar'

// AppShell — the persistent frame of every authenticated screen
// (design/components/app-shell.html): a navigation rail (ALWAYS dark, a
// constant brand anchor), a themed top bar (breadcrumb + bell), and a themed
// content area. Tenant sessions show a WorkspaceChip (accent dot); internal
// INHERTA sessions show a ContextChip (brand gradient).
//
// Responsive: at lg+ the rail is persistent and collapses to 60px. Below lg
// (tablet + phone) the rail is REMOVED entirely and the top bar grows a
// hamburger button that opens the full labelled nav in a left drawer — the
// classic mobile pattern, not a cramped icon rail.

function ChevDown({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={cn('size-[13px]', className)}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  )
}

function BrandMark({
  collapsed,
  onBrandClick,
}: {
  collapsed: boolean
  onBrandClick?: (() => void) | undefined
}) {
  const { t } = useTranslation('components')
  // Collapsed shows the isotipo, which is the A of the wordmark — the same mark
  // either way, not a substitute glyph. Sized on the <svg>, since these carry a
  // viewBox and no width/height and a sized wrapper would collapse them to zero.
  const wordmark = collapsed ? (
    <InhertaIcon className="h-[19px] w-auto" />
  ) : (
    <InhertaWordmark className="h-[17px] w-auto text-white" />
  )
  if (!onBrandClick) return wordmark
  return (
    <button
      type="button"
      onClick={onBrandClick}
      aria-label={t('ui.goHome')}
      className="-mx-1 flex items-center rounded-md px-1 py-0.5 transition-colors hover:bg-white/[0.06] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-blue"
    >
      {wordmark}
    </button>
  )
}

function WorkspaceChip({
  name,
  accent,
  collapsed,
  onClick,
}: {
  name: string
  accent: string
  collapsed: boolean
  onClick?: (() => void) | undefined
}) {
  const dot = (
    <span
      className="size-2 flex-none rounded-full"
      style={{
        backgroundColor: accent,
        boxShadow: `0 0 0 3px color-mix(in srgb, ${accent} 18%, transparent)`,
      }}
      aria-hidden
    />
  )
  const layout = collapsed ? 'mx-auto size-[34px] justify-center p-0' : 'mx-3 px-[11px] py-[9px]'

  // Without an onClick there's no workspace switcher to open, so render a static
  // chip (no button affordance, no chevron) rather than a dead button.
  if (!onClick) {
    return (
      <div
        className={cn(
          'mb-2 mt-1.5 flex items-center gap-[9px] rounded-lg border border-white/[0.06] bg-white/[0.045]',
          layout,
        )}
      >
        {dot}
        {!collapsed && (
          <span className="truncate text-[12px] font-semibold text-[#e7eaf0]">{name}</span>
        )}
      </div>
    )
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={collapsed ? `Espacio de trabajo: ${name}` : undefined}
      className={cn(
        'mb-2 mt-1.5 flex items-center gap-[9px] rounded-lg border border-white/[0.06] bg-white/[0.045] transition-colors hover:bg-white/[0.08]',
        layout,
      )}
    >
      {dot}
      {!collapsed && (
        <>
          <span className="truncate text-[12px] font-semibold text-[#e7eaf0]">{name}</span>
          <ChevDown className="ml-auto flex-none text-[#7e8795]" />
        </>
      )}
    </button>
  )
}

function ContextChip({ collapsed }: { collapsed: boolean }) {
  return (
    <div
      className={cn(
        'mb-2 mt-1.5 flex items-center gap-[9px] rounded-lg',
        collapsed
          ? 'mx-auto h-[34px] w-[38px] justify-center'
          : 'mx-3 border border-[color-mix(in_srgb,var(--brand-blue)_38%,transparent)] bg-[linear-gradient(135deg,color-mix(in_srgb,var(--brand-blue)_24%,transparent),color-mix(in_srgb,var(--brand-blue-deep)_28%,transparent))] px-2.5 py-2',
      )}
    >
      <span className="size-6 flex-none rounded-md bg-[image:var(--brand-gradient)]" aria-hidden />
      {!collapsed && (
        <span className="leading-tight">
          <span className="block text-[8.5px] font-bold uppercase tracking-[0.13em] text-[#9aa3b2]">
            Plataforma
          </span>
          <span className="block text-[12px] font-bold text-white">INHERTA interno</span>
        </span>
      )}
    </div>
  )
}

function SidebarToggle({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={!collapsed}
      aria-label={collapsed ? 'Expandir barra lateral' : 'Colapsar barra lateral'}
      className={cn(
        'mb-1.5 flex items-center gap-2.5 rounded-lg text-[11.5px] font-medium text-[#7e8795] transition-colors hover:bg-white/[0.04] hover:text-[#e7eaf0] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-blue',
        collapsed ? 'mx-auto size-[38px] justify-center p-0' : 'mx-3 px-2.5 py-[7px]',
      )}
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
        className={cn(
          'size-[15px] flex-none transition-transform duration-200 motion-reduce:transition-none',
          collapsed && 'rotate-180',
        )}
      >
        <path d="m15 6-6 6 6 6" />
      </svg>
      {!collapsed && <span>Colapsar</span>}
    </button>
  )
}

function UserChip({
  user,
  collapsed,
  onClick,
}: {
  user: AppShellUser
  collapsed: boolean
  onClick?: (() => void) | undefined
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={collapsed ? `Cuenta: ${user.name}` : undefined}
      className={cn(
        'flex items-center gap-[9px] rounded-[10px] text-left transition-colors',
        collapsed
          ? 'mx-auto mb-3.5 size-[38px] justify-center p-0'
          : 'mx-3 mb-3.5 border border-white/[0.06] bg-white/[0.04] px-2.5 py-[9px] hover:bg-white/[0.08]',
      )}
    >
      <span
        className="flex size-[30px] flex-none items-center justify-center overflow-hidden rounded-full bg-[image:var(--brand-gradient)] text-[11px] font-bold text-white"
        aria-hidden
      >
        {user.avatarUrl ? (
          <img src={user.avatarUrl} className="size-full rounded-full object-cover" alt="" />
        ) : (
          user.initials
        )}
      </span>
      {!collapsed && (
        <>
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-[12px] font-semibold text-[#e7eaf0]">{user.name}</span>
            <span className="text-[10.5px] text-[#7e8795]">{user.role}</span>
          </span>
          <ChevDown className="ml-auto flex-none text-[#7e8795]" />
        </>
      )}
    </button>
  )
}

function CloseIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      aria-hidden
    >
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  )
}

// The shared inner content of the rail — rendered both in the persistent desktop
// aside and inside the mobile drawer, so the two never drift.
function SidebarBody({
  context,
  workspace,
  nav,
  activeId,
  user,
  collapsed,
  onNavSelect,
  onWorkspaceClick,
  onUserClick,
  brand,
  onBrandClick,
  toggle,
}: {
  context: 'tenant' | 'admin'
  workspace?: { name: string; accent: string } | undefined
  nav: SidebarNavItem[]
  activeId: string
  user: AppShellUser
  collapsed: boolean
  onNavSelect?: ((id: string) => void) | undefined
  onWorkspaceClick?: (() => void) | undefined
  onUserClick?: (() => void) | undefined
  brand?: ReactNode
  onBrandClick?: (() => void) | undefined
  toggle?: ReactNode
}) {
  return (
    <>
      <div
        className={cn(
          'flex items-center gap-[9px] pb-2 pt-4',
          collapsed ? 'justify-center px-0' : 'px-4',
        )}
      >
        {brand ?? <BrandMark collapsed={collapsed} onBrandClick={onBrandClick} />}
      </div>

      {context === 'tenant' && workspace ? (
        <WorkspaceChip
          name={workspace.name}
          accent={workspace.accent}
          collapsed={collapsed}
          onClick={onWorkspaceClick}
        />
      ) : context === 'admin' ? (
        <ContextChip collapsed={collapsed} />
      ) : null}

      <SidebarNav
        items={nav}
        activeId={activeId}
        collapsed={collapsed}
        onItemSelect={onNavSelect}
      />
      {toggle}
      <UserChip user={user} collapsed={collapsed} onClick={onUserClick} />
    </>
  )
}

export interface AppShellUser {
  name: string
  role: string
  initials: string
  avatarUrl?: string | undefined
}

export interface AppShellProps {
  context: 'tenant' | 'admin'
  /** Tenant sessions only — renders the WorkspaceChip. */
  workspace?: { name: string; accent: string }
  nav: SidebarNavItem[]
  activeId: string
  user: AppShellUser
  breadcrumb: TopBarBreadcrumbItem[]
  collapsed?: boolean
  onToggleCollapse: () => void
  onNavSelect?: (id: string) => void
  hasNotifications?: boolean
  notificationCount?: number | undefined
  onBellClick?: () => void
  onWorkspaceClick?: () => void
  onUserClick?: () => void
  /** Navigate home when the brand wordmark is clicked. */
  onBrandClick?: () => void
  topBarActions?: ReactNode
  /** Brand mark slot (logo). Defaults to the INHERTA wordmark. */
  brand?: ReactNode
  children: ReactNode
  className?: string
}

/**
 * Persistent frame of every authenticated screen: always-dark nav rail (drawer
 * below lg), themed TopBar and content area. `context` picks WorkspaceChip
 * (tenant) vs ContextChip (admin); `collapsed` drives rail width at lg+ only.
 */
export function AppShell({
  context,
  workspace,
  nav,
  activeId,
  user,
  breadcrumb,
  collapsed = false,
  onToggleCollapse,
  onNavSelect,
  hasNotifications,
  notificationCount,
  onBellClick,
  onWorkspaceClick,
  onUserClick,
  onBrandClick,
  topBarActions,
  brand,
  children,
  className,
}: AppShellProps) {
  const { t } = useTranslation('components')
  // Below lg (tablet + phone) the persistent rail is removed; navigation moves
  // into a hamburger-triggered drawer. At/above lg the rail is persistent and
  // the `collapsed` prop (the user toggle) drives its width.
  const compact = useIsBelowLg()
  const [mobileOpen, setMobileOpen] = useState(false)

  function closeMobile(): void {
    setMobileOpen(false)
  }

  return (
    <div className={cn('flex h-full min-h-0', className)}>
      <a
        href="#main-content"
        className="sr-only focus:absolute focus:left-2 focus:top-2 focus:z-[70] focus:not-sr-only focus:rounded-md focus:bg-surface focus:px-3 focus:py-1.5 focus:text-[12px] focus:font-semibold focus:text-text focus:shadow-card"
      >
        Saltar al contenido
      </a>

      {!compact && (
        <aside
          className={cn(
            'flex flex-none flex-col overflow-hidden border-r border-white/[0.06] bg-chrome transition-[width] duration-normal ease-vysto motion-reduce:transition-none',
            collapsed ? 'w-[60px]' : 'w-[226px]',
          )}
        >
          <SidebarBody
            context={context}
            workspace={workspace}
            nav={nav}
            activeId={activeId}
            user={user}
            collapsed={collapsed}
            onNavSelect={onNavSelect}
            onWorkspaceClick={onWorkspaceClick}
            onUserClick={onUserClick}
            brand={brand}
            onBrandClick={onBrandClick}
            toggle={<SidebarToggle collapsed={collapsed} onToggle={onToggleCollapse} />}
          />
        </aside>
      )}

      <div className="flex min-w-0 flex-1 flex-col bg-bg">
        <TopBar
          breadcrumb={breadcrumb}
          actions={topBarActions}
          hasNotifications={hasNotifications}
          notificationCount={notificationCount}
          onBellClick={onBellClick}
          {...(compact ? { onMenuClick: () => setMobileOpen(true) } : {})}
        />
        <main
          id="main-content"
          className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-[22px]"
        >
          {children}
        </main>
      </div>

      {compact && (
        <Dialog.Root open={mobileOpen} onOpenChange={(o) => !o && setMobileOpen(false)}>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-40 bg-[rgba(8,10,14,0.55)]" />
            <Dialog.Content
              aria-describedby={undefined}
              className="fixed inset-y-0 left-0 z-50 flex w-[260px] max-w-[82vw] flex-col bg-chrome shadow-modal focus:outline-none"
            >
              <Dialog.Title className="sr-only">{t('ui.sidebar')}</Dialog.Title>
              <Dialog.Close asChild>
                <button
                  type="button"
                  aria-label={t('ui.closeMenu')}
                  className="absolute right-2.5 top-4 flex size-8 items-center justify-center rounded-md text-[#9aa3b2] transition-colors hover:bg-white/[0.06] hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-blue [&_svg]:size-[15px]"
                >
                  <CloseIcon />
                </button>
              </Dialog.Close>
              <SidebarBody
                context={context}
                workspace={workspace}
                nav={nav}
                activeId={activeId}
                user={user}
                collapsed={false}
                onNavSelect={(id) => {
                  closeMobile()
                  onNavSelect?.(id)
                }}
                {...(onWorkspaceClick
                  ? {
                      onWorkspaceClick: () => {
                        closeMobile()
                        onWorkspaceClick()
                      },
                    }
                  : {})}
                {...(onUserClick
                  ? {
                      onUserClick: () => {
                        closeMobile()
                        onUserClick()
                      },
                    }
                  : {})}
                brand={brand}
                {...(onBrandClick
                  ? {
                      onBrandClick: () => {
                        closeMobile()
                        onBrandClick()
                      },
                    }
                  : {})}
              />
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}
    </div>
  )
}
